import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import Loader from "components/Atoms/SharedComponents/Loader/Loader";
import { toast } from "react-toastify";
import setAuthToken from "services/modules/utils/handel_api";
import BannersTemplate from "templates/lookups/Banners";

import { GetScrollVendorsRequest } from "services/modules/vendors";
import { GetScrollOccasionTypesRequest } from "services/modules/occasions/occasionsTypes";
import { GetScrollCategoriesRequest } from "services/modules/lookups/categories";
import { getScrollTagsRequest } from "services/modules/lookups/tags";
import { getScrollProductsRequest } from "services/modules/products";

import {
  getBannersRequest,
  createBannerRequest,
  deleteBannerRequest,
  getBannerDetailsRequest,
  updateBannerRequest,
} from "services/modules/lookups/banners";

function BannersPage() {
  const { t } = useTranslation();
  const token = localStorage.getItem("token");
  setAuthToken(token);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  let [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const type = searchParams.get("type");

  const list = useSelector((state) => state.banners.banners);
  const count = useSelector((state) => state.banners.count);
  const details = useSelector((state) => state.banners.banner);
  const load = useSelector((state) => state.banners.load);

  // Lookup selections
  const vendors = useSelector((state) => state.vendors.filterVendors);
  const vendorsCount = useSelector((state) => state.vendors.listCount);
  const vendorsLoading = useSelector((state) => state.vendors.load);

  const occasions = useSelector((state) => state.occasionsTypes.filterOccation);
  const occasionsCount = useSelector((state) => state.occasionsTypes.listCount);
  const occasionsLoading = useSelector((state) => state.occasionsTypes.load);

  const categories = useSelector((state) => state.categories.filterCategories);
  const categoriesCount = useSelector((state) => state.categories.listCount);
  const categoriesLoading = useSelector((state) => state.categories.load);

  const tags = useSelector((state) => state.tags.filterTags);
  const tagsCount = useSelector((state) => state.tags.listCount);
  const tagsLoading = useSelector((state) => state.tags.load);

  const products = useSelector((state) => state.products.filterProducts) || [];
  const productsCount = useSelector((state) => state.products.listCount);
  const productsLoading = useSelector((state) => state.products.load);

  const fetchVendors = (filterObj) => {
    const requestData = {
      params: {
        hide_details: true,
        skip_pagination: true,
        ...filterObj,
      },
    };
    dispatch(GetScrollVendorsRequest(requestData));
  };

  const fetchOccassions = (filterObj) => {
    const requestData = {
      params: {
        hide_details: true,
        skip_pagination: true,
        ...filterObj,
      },
    };
    dispatch(GetScrollOccasionTypesRequest(requestData));
  };

  const fetchCategories = (filterObj) => {
    const requestData = {
      params: {
        hide_details: true,
        skip_pagination: true,
        ...filterObj,
      },
    };
    dispatch(GetScrollCategoriesRequest(requestData));
  };

  const fetchTags = (filterObj) => {
    const requestData = {
      params: {
        hide_details: true,
        skip_pagination: true,
        ...filterObj,
      },
    };
    dispatch(getScrollTagsRequest(requestData));
  };

  const fetchProducts = (filterObj) => {
    dispatch(
      getScrollProductsRequest({
        params: {
          skip_pagination: true,
          ...filterObj,
        },
      }),
    );
  };

  const options = {
    1: {
      options: products,
      onOpen: products?.length <= 1 && fetchProducts,
      isLoading:
        productsLoading?.loading &&
        productsLoading?.type === "filter-products-list",
      getList: fetchProducts,
      applySearchFromRequest: true,
      count: productsCount,
    },
    2: {
      options: vendors,
      onOpen: vendors?.length <= 1 && fetchVendors,
      isLoading: vendorsLoading?.loading,
      getList: fetchVendors,
      applySearchFromRequest: true,
      count: vendorsCount,
    },
    3: {
      options: categories,
      onOpen: categories?.length <= 1 && fetchCategories,
      isLoading: categoriesLoading?.loading,
      getList: fetchCategories,
      applySearchFromRequest: true,
      count: categoriesCount,
    },
    4: {
      options: occasions,
      onOpen: occasions?.length <= 1 && fetchOccassions,
      isLoading: occasionsLoading?.loading,
      getList: fetchOccassions,
      applySearchFromRequest: true,
      count: occasionsCount,
    },
    5: {
      options: tags,
      onOpen: tags?.length <= 1 && fetchTags,
      isLoading: tagsLoading?.loading,
      getList: fetchTags,
      applySearchFromRequest: true,
      count: tagsCount,
    },
  };

  //permissions
  const currentUser = useSelector(
    (state) => state.authenticate.currentUserProfile
  );
  const permissions = useSelector(
    (state) =>
      state.authenticate.currentUserProfile.permissions.featured_banners
  );
  const isSuperAdmin = currentUser?.privilege == "super_admin";

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  const [page, setPage] = useState(1);
  const [filterObj, setFilterObj] = useState(null);

  const handlePageChange = (page) => {
    setPage(page);
    setFilterObj((prev) => {
      return { ...prev, page_number: page };
    });
  };

  const getDetails = async () => {
    dispatch(getBannerDetailsRequest({ id }));
  };

  const getList = async (filterObj) => {
    const requestData = {
      params: {
        page_size: 10,
        page_number: 1,
        keyword: "",
        sort_by: "id",
        ...filterObj,
      },
    };
    dispatch(getBannersRequest(requestData));
  };

  // mappedData use for create custom cell to concat multi values or navigate button
  const mappedData = list?.map((data, index) => {
    return {
      ...data,
      name: "Banner",
      featuredAdEn: (
        <img
          src={data?.localized_data?.en?.banner}
          alt={data?.name}
          width="125px"
          height="77px"
        />
      ),
      featuredAdAr: (
        <img
          src={data?.localized_data?.ar?.banner}
          alt={data?.name}
          width="125px"
          height="77px"
        />
      ),
    };
  });

  const rowData = (data) => {
    setSelectedRow(data);
  };

  const breadcrumbs = [
    {
      active: true,
      path: "/",
      title: t("common.home"),
    },
    {
      active: !type ? false : true,
      path: "/featured-ads",
      title: t("featured_ads.title"),
    },
    type && {
      active: false,
      title:
        type === "details"
          ? t("featured_ads.details")
          : id
          ? t("featured_ads.edit")
          : t("featured_ads.add"),
    },
  ];
  const handelSubmit = (data, formik) => {
    const {
      featuredAdAr,
      featuredAdEn,
      is_visible,
      need_action,
      data: dataNum,
      data_id,
    } = data;
    const action = () => {
      searchParams.delete("id");
      searchParams.delete("type");
      navigate("/featured-ads");
      !id && formik?.resetForm();
      // USE TOSTIFY TO CONFIRM ACTION
      toast.success(
        id ? t("featured_ads.updated") : t("featured_ads.created")
      );
    };

    const typeMapReverse = {
      1: "products",
      2: "stores",
      3: "categories",
      4: "occasions",
      5: "tags",
    };

    const requestData = {
      body: {
        featured_banner: {
          en: {
            banner: featuredAdEn,
          },
          ar: {
            banner: featuredAdAr,
          },
          need_action,
          is_visible,
          data: typeMapReverse[dataNum] || "",
          data_id,
        },
      },
      action,
    };

    if (id) {
      //update call
      dispatch(updateBannerRequest({ ...requestData, id }));
    } else {
      //create call
      dispatch(createBannerRequest(requestData));
    }
  };

  const filterList = [];

  const handleDelete = (id) => {
    const action = () => {
      if (list.length === 1 && page !== 1) {
        setPage(page - 1);
        setFilterObj((prev) => ({
          ...prev,
          page_number: page - 1,
        }));
      } else {
        getList(filterObj);
      }
      type === "details" && navigate("/banners");
      toast.success(t("featured_ads.deleted"));
    };
    const requestData = {
      id,
      action, //dispatched on success
    };
    dispatch(deleteBannerRequest(requestData));
    setOpenDeleteModal(false);
  };

  //list of actions to be displayed when clicked in the 3 dots
  const actionsList = ({ index, data }) => {
    return [
      {
        condition: () => {
          return true;
        },
        click: () => {
          navigate(`?type=details&id=${data?.id}`);
        },
        text: t("common.view"),
      },
      {
        condition: () => {
          return isSuperAdmin || permissions?.can_update;
        },
        click: () => {
          navigate(`?type=form&id=${data?.id}`);
        },
        text: t("common.edit"),
      },
      {
        condition: () => {
          return isSuperAdmin || permissions?.can_delete;
        },
        click: () => {
          setOpenDeleteModal(true);
        },
        text: t("common.delete"),
      },
    ];
  };

  //if you need more buttons in the header beside the add button
  const headerChildren = <></>;

  //if more modals needed, pass them to the modalComponents as below
  const modalComponents = [];

  //listing all tags
  useEffect(() => {
    if (filterObj) getList(filterObj);
  }, [filterObj]);

  //details of a single tag
  useEffect(() => {
    if (id) getDetails();
  }, [id]);

  return (
    <>
      <Loader open={load?.loading} />
      <BannersTemplate
        filterObj={filterObj}
        setFilterObj={setFilterObj}
        data={mappedData}
        count={count}
        paginationPage={page}
        handlePageChange={handlePageChange}
        rowData={rowData}
        details={details}
        breadcrumbs={breadcrumbs}
        filterList={filterList}
        handelSubmit={handelSubmit}
        actionsList={actionsList}
        headerChildren={headerChildren}
        openDeleteModal={openDeleteModal}
        setOpenDeleteModal={setOpenDeleteModal}
        handleDeleteRequest={handleDelete}
        modalComponents={modalComponents}
        selectedRow={selectedRow}
        can_create={isSuperAdmin || permissions?.can_create}
        options={options}
      />
    </>
  );
}

export default BannersPage;
